/// <reference path="../../styles/theme.d.ts" />
import {styled} from '@mui/material/styles';
import Box, {BoxProps} from '@mui/material/Box';
import Typography, {TypographyProps} from '@mui/material/Typography';
import Chip from '@mui/material/Chip';

export interface ToggleFilterProps<T> extends Omit<BoxProps, 'children' | 'onToggle'> {
  label: string;
  options: T[];
  selectedValues: T[];
  onToggle: (value: T) => void;
  getLabel?: (value: T) => string;
  minOptionsToShow?: number;
}

const StyledToggleFilterContainer = styled(Box, {
  name: 'ToggleFilter',
})<BoxProps>(({theme}) => ({
  // Apply theme overrides
  ...theme.components?.ToggleFilter?.styleOverrides?.root,
}));

const StyledToggleFilterLabel = styled(Typography, {
  name: 'ToggleFilter',
  slot: 'Label',
})<TypographyProps>(({theme}) => ({
  marginBottom: theme.spacing(1),
  fontWeight: 500,
  // Apply theme overrides
  ...theme.components?.ToggleFilter?.styleOverrides?.label,
}));

const StyledToggleFilterChipsContainer = styled(Box, {
  name: 'ToggleFilter',
  slot: 'ChipsContainer',
})<BoxProps>(({theme}) => ({
  display: 'flex',
  flexWrap: 'wrap',
  gap: theme.spacing(1),
  // Apply theme overrides
  ...theme.components?.ToggleFilter?.styleOverrides?.chipsContainer,
}));

export default function ToggleFilter<T extends string | number>({
  label,
  options,
  selectedValues,
  onToggle,
  getLabel,
  minOptionsToShow = 2,
  sx,
  ...props
}: ToggleFilterProps<T>) {
  // Only show if there are enough options
  if (options.length < minOptionsToShow) {
    return null;
  }

  return (
    <StyledToggleFilterContainer sx={sx} {...props}>
      <StyledToggleFilterLabel variant="body2" color="text.secondary">
        {label}
      </StyledToggleFilterLabel>
      <StyledToggleFilterChipsContainer>
        {options.map((option) => {
          const isSelected = selectedValues.includes(option);
          const displayLabel = getLabel ? getLabel(option) : String(option);
          return (
            <Chip
              key={String(option)}
              label={displayLabel}
              onClick={() => onToggle(option)}
              color={isSelected ? 'primary' : 'default'}
              variant={isSelected ? 'filled' : 'outlined'}
              sx={{
                cursor: 'pointer',
                fontWeight: isSelected ? 600 : 400,
              }}
            />
          );
        })}
      </StyledToggleFilterChipsContainer>
    </StyledToggleFilterContainer>
  );
}
